import { faFlagSwallowtail as faFlagSwallowtailLight } from '@fortawesome/pro-light-svg-icons';
import { faFlagSwallowtail } from '@fortawesome/pro-solid-svg-icons';

import { ProgramItem, ProgramsCreator } from '../model/programs.types';

import {
  AddToOfferAction,
  AddToOfferDirectAction,
  ChallengeIndicator,
  ProgramCardRow,
  ProgramsFilters,
  ProgramsVM,
  RemoveFromSubscriptionAction,
  RowMenuItem,
  ViewMode,
} from './programs.viewModel.types';

export const applyFilters = (
  programs: ReadonlyArray<ProgramItem>,
  filters: ProgramsFilters,
  searchText: string,
): Array<ProgramItem> => {
  const normalizedSearch = searchText.trim().toLowerCase();

  return programs.filter((program) => {
    const matchesStatus =
      filters.status === 'all' || program.state === filters.status;
    const matchesSoldAs =
      filters.soldAs === 'all' || program.offerState.type === filters.soldAs;
    const matchesSearch =
      normalizedSearch === '' ||
      program.name.toLowerCase().includes(normalizedSearch);

    return matchesStatus && matchesSoldAs && matchesSearch;
  });
};

export const toPersistedIndexOrder = (
  programs: ReadonlyArray<ProgramItem>,
): Array<ProgramItem> =>
  [...programs].sort((a, b) => {
    const aIndex = a.index ?? Number.MAX_SAFE_INTEGER;
    const bIndex = b.index ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });

export const toViewMode = (
  totalPrograms: number,
  filteredPrograms: number,
  rowsBuilder: () => Array<ProgramCardRow>,
): ViewMode => {
  if (totalPrograms === 0) return { type: 'empty' };
  if (filteredPrograms === 0) return { type: 'no-results' };
  return { type: 'list', rows: rowsBuilder() };
};

const createMenuItem = ({
  label,
  onClick,
  destructive,
  disabled,
  disabledReason,
  testId,
}: {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  testId?: string;
}): RowMenuItem => ({
  label,
  onClick,
  destructive,
  disabled,
  disabledReason,
  testId,
});

export const buildRowMenu = (
  program: ProgramItem,
  creator: ProgramsCreator,
  dispatch: ProgramsVM['dispatch'],
): Array<RowMenuItem> => {
  const items: Array<RowMenuItem> = [
    createMenuItem({
      label: 'Edit',
      onClick: () => dispatch('EditProgram', program.id),
      testId: 'program-menu-edit',
    }),
    createMenuItem({
      label: 'Duplicate',
      onClick: () => dispatch('DuplicateProgram', program.id),
      testId: 'program-menu-duplicate',
    }),
  ];

  // Archive is available in every lifecycle state except the terminal one.
  // DRAFT / READY / PUBLISHED programs can all be archived (the cascade
  // rules per §4.3 of the business-logic spec are enforced in Unit 3 via
  // the ArchiveInDraftOfferModal trigger condition).
  if (program.state !== 'archived') {
    items.push(
      createMenuItem({
        label: 'Archive',
        onClick: () =>
          dispatch('ArchiveProgram', { id: program.id, name: program.name }),
        destructive: true,
        testId: 'program-menu-archive',
      }),
    );
  }

  // Hard delete is only allowed on a DRAFT Program that is not attached to
  // any offer. The server still enforces its own narrower rule (locked
  // exclusive blocker) as a safety net; this guard removes the affordance
  // from creators it doesn't apply to, rather than relying on the server
  // error.
  if (program.state === 'draft' && program.offerState.type === 'none') {
    items.push(
      createMenuItem({
        label: 'Delete',
        onClick: () =>
          dispatch('DeleteProgram', { id: program.id, name: program.name }),
        destructive: true,
        testId: 'program-menu-delete',
      }),
    );
  }

  return items;
};

export const buildRemoveFromSubscriptionAction = (
  program: ProgramItem,
  creator: ProgramsCreator,
  dispatch: ProgramsVM['dispatch'],
): RemoveFromSubscriptionAction | null => {
  if (creator.mode !== 'legacy') return null;
  if (program.offerState.type !== 'subscription') return null;
  return {
    label: 'Remove from subscription',
    onClick: () =>
      dispatch('RemoveFromSubscription', {
        id: program.id,
        name: program.name,
      }),
    testId: 'programs-remove-from-subscription-action',
  };
};

export const buildAddToOfferActions = (
  program: ProgramItem,
  creator: ProgramsCreator,
  dispatch: ProgramsVM['dispatch'],
): Array<AddToOfferAction> => {
  if (program.offerState.type !== 'none') return [];
  if (program.state === 'archived') return [];

  if (creator.mode === 'new') {
    return [
      {
        key: 'new-creator-subscription',
        label: 'Sell with Subscription',
        onClick: () => dispatch('AddToOfferNavigate'),
        destination: 'offers',
        testId: 'programs-add-to-offer-subscription',
      },
      {
        key: 'new-creator-otp',
        label: 'Sell as OTP Offer',
        onClick: () => dispatch('AddToOfferNavigate'),
        destination: 'offers',
        testId: 'programs-add-to-offer-otp',
      },
    ];
  }

  return [
    {
      key: 'legacy-subscription',
      label: 'Sell with Subscription',
      onClick: () =>
        dispatch('SellWithSubscription', {
          id: program.id,
          name: program.name,
        }),
      destination: 'subscription',
      testId: 'programs-add-to-offer-subscription',
    },
    {
      key: 'legacy-otp',
      label: 'Sell as OTP Offer',
      onClick: () => dispatch('AddToOfferNavigate'),
      destination: 'offers',
      testId: 'programs-add-to-offer-otp',
    },
  ];
};

const toAddToOfferDirectAction = (
  actions: ReadonlyArray<AddToOfferAction>,
): AddToOfferDirectAction | null => {
  if (actions.length === 0) return null;
  if (actions.some((action) => action.disabled)) return null;
  if (!actions.every((action) => action.destination === 'offers')) return null;

  return {
    onClick: actions[0].onClick,
    testId: 'programs-add-to-offer-direct',
  };
};

// Intentionally uses `program.status` (wire published state), not `program.state`
// (derived 4-state lifecycle) — the tooltip is semantically tied to the wire
// published state to detect programs that are published but invisible to consumers.
export const toTooltipText = (program: ProgramItem): string | null => {
  if (program.status !== 'published') return null;
  if (!program.notVisibleToConsumers) return null;
  if (program.programBusinessModel === 'subscription') {
    return 'The program is published. To make it available in your app, add it to a subscription offer.';
  }
  return 'The program is published. To make it available in your app, add it to your offer and put it on sale.';
};

export const toChallengeIndicator = (
  program: Pick<ProgramItem, 'hasChallenges' | 'hasOngoingChallenges'>,
): ChallengeIndicator | null => {
  if (!program.hasChallenges) return null;
  if (program.hasOngoingChallenges) {
    return {
      icon: faFlagSwallowtail,
      tooltip: 'This program has ongoing challenge',
      color: 'G-100',
    };
  }
  return {
    icon: faFlagSwallowtailLight,
    tooltip: 'This program has past or future challenges',
    color: 'N-60',
  };
};

export type CardRowDeps = {
  dispatch: ProgramsVM['dispatch'];
  creator: ProgramsCreator;
};

export const toCardRow = (
  program: ProgramItem,
  deps: CardRowDeps,
): ProgramCardRow => {
  const addToOfferActions = buildAddToOfferActions(
    program,
    deps.creator,
    deps.dispatch,
  );
  const addToOfferDirectAction = toAddToOfferDirectAction(addToOfferActions);

  return {
    id: program.id,
    name: program.name,
    state: program.state,
    offerState: program.offerState,
    metrics: program.metrics,
    commentsCount: program.commentsCount,
    mediaCover: program.mediaCover,
    subInfo: program.subInfo,
    menuItems: buildRowMenu(program, deps.creator, deps.dispatch),
    addToOfferActions: addToOfferDirectAction ? [] : addToOfferActions,
    addToOfferDirectAction,
    removeFromSubscriptionAction: buildRemoveFromSubscriptionAction(
      program,
      deps.creator,
      deps.dispatch,
    ),
    isReorderPending: false,
    notVisibleToConsumers: program.notVisibleToConsumers,
    tooltipText: toTooltipText(program),
    challengeIndicator: toChallengeIndicator(program),
    readiness: program.readiness,
  };
};

export const toCardRows = (
  programs: ReadonlyArray<ProgramItem>,
  deps: CardRowDeps,
): Array<ProgramCardRow> => programs.map((program) => toCardRow(program, deps));

export const toSalesTypeFromSoldAsFilter = (
  soldAs: ProgramsFilters['soldAs'],
): ProgramItem['salesType'] | null => {
  if (soldAs === 'subscription') return 'subscription';
  if (soldAs === 'otp') return 'otp';
  return null;
};
